/**
 * Payments Dashboard Page
 *
 * Displays Manifold (5 Axis Payments) dashboard data including:
 * - Total billing and new billing
 * - Subscription counts by plan
 * - Signups trends
 * - Payment and refund totals
 */

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui'
import { paymentsAPI } from '../services/api'
import {
  toAmountTrend, toCountTrend, currentBucket, currentValue, periodDelta, currentRatio, periodLabel,
} from '../lib/manifoldDashboard'
import { drugOf, billingFrequencyOf, groupPlans } from '../lib/planTaxonomy'
import DeclinesSection from '../components/DeclinesSection'
import GatewaySection from '../components/GatewaySection'
import TenderMixSection from '../components/TenderMixSection'
import { useAuthStore } from '../stores/authStore'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  ReferenceLine
} from 'recharts'
import {
  DollarSign,
  Users,
  CreditCard,
  RotateCcw,
  RefreshCw,
  AlertCircle,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  XCircle,
  Pill,
  CalendarClock,
  Wallet
} from 'lucide-react'

const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

// Color palette for charts
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

// Dashboard payload keys → what a reader would call them. Used to name the
// endpoints that failed instead of reporting an anonymous "some are down",
// which reads identically whether one endpoint broke or the token was rejected.
const ENDPOINT_LABELS = {
  billing: 'Billing',
  newBilling: 'New billing',
  subscriptionCounts: 'Subscription counts',
  signups: 'Signups',
  payments: 'Payments',
  refunds: 'Refunds',
}

/**
 * Format a dollar amount.
 *
 * Every money figure on this page comes from Manifold's dashboard endpoints,
 * which divide by 100 before responding (`totalAmount / 100` throughout
 * app/routers/dashboard.py). This used to divide by 100 again, so anything that
 * did render read a hundredth of the real number.
 */
function formatCurrency(dollars, compact = false) {
  if (compact && Math.abs(dollars) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(dollars)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(dollars)
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Summary Card Component
 */
/**
 * One dimension of the subscription base as a donut.
 *
 * Deliberately NOT labelled on the slices. The old chart wrote
 * "name: 12.3%" onto each wedge and then repeated the whole thing as a legend
 * list underneath — the same numbers twice, with the labels overlapping
 * wherever two plans were close in size. The legend carries the numbers; the
 * donut carries the shape.
 */
function MixDonut({ title, icon: Icon, data, loading, emptyMessage, note }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5" />}
          {title}
        </CardTitle>
        {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-56 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : data.length > 0 ? (
          <>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={1}
                    dataKey="count"
                    nameKey="name"
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        // Unclassified is grey wherever it appears, so it never
                        // reads as a product with a colour of its own.
                        fill={entry.name === 'Unclassified' ? '#9ca3af' : COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {data.map((item, index) => (
                <div key={item.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: item.name === 'Unclassified' ? '#9ca3af' : COLORS[index % COLORS.length] }}
                    />
                    <span className="truncate">{item.name}</span>
                  </span>
                  <span className="font-medium tabular-nums flex-shrink-0 ml-2">
                    {formatNumber(item.count)} ({item.percentage.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-56 flex items-center justify-center text-muted-foreground text-sm">
            {emptyMessage}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * The change against the prior period, or nothing at all.
 *
 * Renders NOTHING when the delta is null rather than a dash or a 0%. Null here
 * means "there is no prior bucket, or it was zero", and a 0% would read as
 * "flat" — a claim about the business rather than an absence of one.
 */
function DeltaBadge({ delta }) {
  if (typeof delta !== 'number' || !Number.isFinite(delta)) return null
  const flat = Math.abs(delta) < 0.05
  const Icon = flat ? Minus : delta > 0 ? TrendingUp : TrendingDown
  const tone = flat
    ? 'text-muted-foreground'
    : delta > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400'
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${tone}`}>
      <Icon className="h-3 w-3" />
      {flat ? '0%' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
    </span>
  )
}

function SummaryCard({ title, value, subtitle, icon: Icon, loading, error, delta }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : error ? (
          <div className="text-center py-2">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Failed to load</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              )}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
              <DeltaBadge delta={delta} />
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Custom Tooltip for Charts
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[150px]">
      <p className="font-medium text-sm mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </span>
            <span className="font-medium ml-4">
              {typeof entry.value === 'number' && entry.name?.toLowerCase().includes('amount')
                ? formatCurrency(entry.value)
                : formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function PaymentsPage() {
  const { clinics, currentClinicId } = useAuthStore()
  const [period, setPeriod] = useState('monthly')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  // Structured plan mix, fetched separately: it exists only for Manifold
  // tenants with Mongo configured, and its absence must degrade to the parsed
  // fallback rather than blanking the donuts.
  const [planMix, setPlanMix] = useState(null)
  // Declines are read from the tenant's own collections, so like the plan mix
  // they can be unavailable while the rest of the page is fine. Their own
  // error state, never the page's.
  const [declines, setDeclines] = useState(null)
  const [declinesError, setDeclinesError] = useState(null)
  const [gateways, setGateways] = useState(null)
  const [tenders, setTenders] = useState(null)
  const [tendersError, setTendersError] = useState(null)
  const [gatewaysError, setGatewaysError] = useState(null)
  const [endpointErrors, setEndpointErrors] = useState(null)
  const [needsClinic, setNeedsClinic] = useState(false)
  const [noTenant, setNoTenant] = useState(null)
  const [tenantId, setTenantId] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  // Nine Manifold tenants share this one screen, so it says whose numbers these
  // are. tenant_id comes from the server — it is the thing actually sent
  // upstream, and the mapping from clinic to tenant is not one-to-one
  // (mdexam and mdexam_legacy are both MDEXAM).
  const clinicName = clinics?.find(c => c.id === currentClinicId)?.name || currentClinicId

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // The structured mix is requested alongside, and its failure is not
      // allowed to fail the page: a clinic without Manifold Mongo simply falls
      // back to the parsed donuts.
      const [response, mixResult, declinesResult, gatewaysResult, tendersResult] = await Promise.all([
        paymentsAPI.getDashboard({ period }),
        paymentsAPI.getPlanMix().catch(() => null),
        paymentsAPI.getDeclines().catch(err => ({ __error: err })),
        paymentsAPI.getGateways().catch(err => ({ __error: err })),
        paymentsAPI.getTenderMix().catch(err => ({ __error: err })),
      ])
      // Same treatment as gateways: its failure loses the panel, not the page.
      if (tendersResult?.__error) {
        setTenders(null)
        setTendersError(tendersResult.__error.response?.data?.error?.message || tendersResult.__error.message)
      } else {
        setTenders(tendersResult?.data?.data || null)
        setTendersError(null)
      }
      setPlanMix(mixResult?.data?.byFrequency ? mixResult.data : null)

      if (declinesResult?.__error) {
        setDeclines(null)
        setDeclinesError(
          declinesResult.__error.response?.data?.error?.message
            || declinesResult.__error.message
        )
      } else {
        setDeclines(declinesResult?.data?.data || null)
        setDeclinesError(null)
      }

      // Same treatment: a clinic without Manifold Mongo loses this panel and
      // keeps the page.
      if (gatewaysResult?.__error) {
        setGateways(null)
        setGatewaysError(
          gatewaysResult.__error.response?.data?.error?.message
            || gatewaysResult.__error.message
        )
      } else {
        setGateways(gatewaysResult?.data?.data || null)
        setGatewaysError(null)
      }
      if (response.data.success) {
        setDashboardData(response.data.data)
        setEndpointErrors(response.data.errors || null)
        setTenantId(response.data.meta?.tenantId || null)
        setNeedsClinic(false)
        setNoTenant(null)
        setLastUpdated(new Date())
      } else {
        setError(response.data.error?.message || 'Failed to load dashboard data')
      }
    } catch (err) {
      console.error('[PAYMENTS] Fetch error:', err)
      const code = err.response?.data?.error?.code
      if (code === 'CLINIC_SELECTION_REQUIRED' || code === 'NO_MANIFOLD_TENANT') {
        setNeedsClinic(code === 'CLINIC_SELECTION_REQUIRED')
        setNoTenant(code === 'NO_MANIFOLD_TENANT' ? err.response.data.error.message : null)
        setDashboardData(null)
        setEndpointErrors(null)
        setTenantId(null)
        return
      }
      setError(err.response?.data?.error?.message || err.message || 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }, [period])

  const checkStatus = useCallback(async () => {
    try {
      const response = await paymentsAPI.getStatus()
      if (response.data.success) {
        setStatus(response.data.data)
        if (response.data.data.tenantId) setTenantId(response.data.data.tenantId)
      }
    } catch (err) {
      console.error('[PAYMENTS] Status check failed:', err)
      const code = err.response?.data?.error?.code
      if (code === 'CLINIC_SELECTION_REQUIRED' || code === 'NO_MANIFOLD_TENANT') {
        setNeedsClinic(code === 'CLINIC_SELECTION_REQUIRED')
        setNoTenant(code === 'NO_MANIFOLD_TENANT' ? err.response.data.error.message : null)
        setStatus(null)
        return
      }
      setStatus({ configured: false, error: err.message })
    }
  }, [])

  useEffect(() => {
    checkStatus()
    fetchData()

    const interval = setInterval(fetchData, AUTO_REFRESH_INTERVAL)
    return () => clearInterval(interval)
    // currentClinicId: the server now scopes this data to the selected clinic,
    // so switching clinics has to refetch rather than leave the previous
    // clinic's numbers on screen.
  }, [fetchData, checkStatus, currentClinicId])

  // Which reports came back empty, and what the server said about each. The
  // dashboard route returns a per-key `errors` object alongside the data; a
  // null value with no matching message still counts as failed so a silent
  // rejection is never mistaken for a clinic that simply has no billing.
  const failedEndpoints = dashboardData
    ? Object.keys(ENDPOINT_LABELS)
        .filter(key => dashboardData[key] === null)
        .map(key => ({ key, message: endpointErrors?.[key] || null }))
    : []
  const allEndpointsFailed = failedEndpoints.length === Object.keys(ENDPOINT_LABELS).length

  // Manifold sends { total_subscriptions, subscriptions: [{ name, num_of_subscriptions, percentage }] },
  // where `name` is a Chargebee entity id carrying three facts at once — the
  // molecule, the billing frequency, and the supply per shipment. Plotting the
  // names themselves gave a wedge per SKU and answered none of those. See
  // lib/planTaxonomy: the same list, split along two axes.
  const allPlans = dashboardData?.subscriptionCounts?.subscriptions

  // Prefer Manifold's structured fields. The dashboard counts endpoint returns
  // only a plan NAME, and a name cannot state a billing cycle reliably: the
  // real ids are `Tirzepatide-Injectable-3-Month-USD` and
  // `Semaglutide-Oral-6-Month-USD--reddit`, so parsing reported Science.co as
  // 100% Monthly when 74 of its 89 live subscriptions bill every 12 weeks.
  //
  // The parsed version stays as the fallback for a clinic whose Manifold Mongo
  // is unreachable — wrong-ish beats blank, as long as the page says which it
  // is showing.
  const drugMix = planMix?.byDrug ?? groupPlans(allPlans, drugOf)
  const frequencyMix = planMix?.byFrequency ?? groupPlans(allPlans, billingFrequencyOf)
  const mixIsStructured = Boolean(planMix)

  const totalSubscriptions = dashboardData?.subscriptionCounts?.total_subscriptions || 0

  // See lib/manifoldDashboard: every trend endpoint answers with a bare ARRAY
  // whose totals are already in dollars. This page used to read `.data`,
  // `.total` and `.count` off those arrays, which are undefined on all of them.
  const billingTrendData = toAmountTrend(dashboardData?.billing)
  const signupsTrendData = toCountTrend(dashboardData?.signups)

  // new_billing is the recurring/new split, and was fetched on every load and
  // rendered nowhere until 2026-09-09. Its shape is undocumented upstream, so
  // it goes through the same adapter as the rest: a payload that is not the
  // expected array becomes [], and the tile reports unavailable rather than $0.
  const newBillingTrendData = toAmountTrend(dashboardData?.newBilling)
  const refundsTrendData = toAmountTrend(dashboardData?.refunds)

  // Payments and refunds take no `period`, so they stay monthly even when the
  // selector says daily.
  const currentBilling = currentValue(billingTrendData, 'amount')
  const currentPayments = currentValue(dashboardData?.payments, 'total')
  const currentRefunds = currentValue(dashboardData?.refunds, 'total')
  const currentNewBilling = currentValue(newBillingTrendData, 'amount')

  // The three numbers the page had but never related to each other.
  // Collection rate is the one that catches a broken payment flow: invoiced
  // money that did not arrive is failed cards and dunning, and it shows up
  // here before it shows up anywhere else.
  // Only meaningful when both sides describe the same window. Billing follows
  // the selector; payments is always monthly. In daily mode this would divide
  // a whole month's collections by a single day's invoicing and report several
  // hundred percent, which looks like a number and is not one.
  const ratiosComparable = period !== 'daily'
  const collectionRate = ratiosComparable
    ? currentRatio(dashboardData?.payments, billingTrendData.map(row => ({ total: row.amount })), 'total')
    : null
  const refundRate = currentRatio(dashboardData?.refunds, dashboardData?.payments, 'total')
  const netRevenue = currentPayments != null ? currentPayments - (currentRefunds || 0) : null

  // Money as one series, so the composed chart can show the gap between
  // invoiced and collected rather than two unrelated cards.
  //
  // Joined on the DATE LABEL, never on array position. Billing follows the
  // period selector and returns ~32 daily buckets in daily mode, while payments
  // and refunds are always 12 monthly ones — so pairing by index would put
  // September the 1st next to last October, silently and plausibly. On a label
  // that finds no match the value is null, recharts draws no bar, and the
  // caption above the chart says why.
  const paymentsByLabel = new Map(
    (Array.isArray(dashboardData?.payments) ? dashboardData.payments : [])
      .map(row => [periodLabel(row), row?.total ?? null])
  )
  const refundsByLabel = new Map(
    (Array.isArray(dashboardData?.refunds) ? dashboardData.refunds : [])
      .map(row => [periodLabel(row), row?.total ?? null])
  )
  const moneyTrendData = billingTrendData.map(row => {
    const refunded = refundsByLabel.get(row.date)
    return {
      date: row.date,
      billed: row.amount,
      collected: paymentsByLabel.get(row.date) ?? null,
      // Negative: it is money going the other way, and drawing it below the
      // axis is the only reading that does not overstate a bad month.
      refunded: refunded ? -refunded : null,
    }
  })

  // Status indicator
  const isConnected = status?.configured && status?.connected

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payments Dashboard</h1>
          <p className="text-muted-foreground">
            Manifold payment processing metrics
            {clinicName && (
              <>
                {' for '}
                <span className="font-medium text-foreground">{clinicName}</span>
                {tenantId && (
                  <span className="ml-1 text-xs font-mono">({tenantId})</span>
                )}
              </>
            )}
            {lastUpdated && (
              <span className="ml-2 text-xs">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Connection Status */}
          <div className="flex items-center gap-2 text-sm">
            {status === null ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : isConnected ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-green-600 dark:text-green-400">Connected</span>
              </>
            ) : status?.configured ? (
              <>
                <XCircle className="h-4 w-4 text-amber-500" />
                <span className="text-amber-600 dark:text-amber-400">Connection Error</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Not Configured</span>
              </>
            )}
          </div>

          {/* Period Toggle */}
          <div className="flex rounded-lg border border-input overflow-hidden">
            <button
              onClick={() => setPeriod('daily')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                period === 'daily'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                period === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              Monthly
            </button>
          </div>

          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Clinic Selection Required */}
      {needsClinic && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Select a Clinic</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Manifold reports billing per tenant, so this dashboard needs a specific
              clinic. Pick one from the clinic switcher to view its payments.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Clinic Is Not On Manifold */}
      {noTenant && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Manifold Tenant</h3>
            <p className="text-muted-foreground max-w-md mx-auto">{noTenant}</p>
          </CardContent>
        </Card>
      )}

      {/* Not Configured State */}
      {!needsClinic && !noTenant && status && !status.configured && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Payments Integration Not Configured</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              The Manifold payments integration is not configured for this environment.
              Contact your administrator to set up the MANIFOLD_MULTI_TOKEN environment variable.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Error State - only show if we have no data at all */}
      {error && status?.configured && !dashboardData && (
        <Card className="border-destructive">
          <CardContent className="py-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
              <div>
                <p className="font-medium text-destructive">Error loading dashboard</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchData} className="ml-auto">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Endpoint Failure Warning - names what failed and why */}
      {!noTenant && failedEndpoints.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
          <CardContent className="py-3">
            <div className="flex items-start gap-2 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0">
                <p className="text-foreground">
                  {allEndpointsFailed
                    ? 'Manifold returned no data for this clinic.'
                    : `Manifold could not serve ${failedEndpoints.length} of ${Object.keys(ENDPOINT_LABELS).length} reports. Displaying available data.`}
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {failedEndpoints.map(({ key, message }) => (
                    <li key={key}>
                      <span className="font-medium text-foreground">{ENDPOINT_LABELS[key] || key}</span>
                      {message ? ` — ${message}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard Content */}
      {!noTenant && !needsClinic && (status?.configured || loading) && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <SummaryCard
              title="Active Subscriptions"
              value={totalSubscriptions ? formatNumber(totalSubscriptions) : '0'}
              subtitle="Total active plans"
              icon={Users}
              loading={loading}
              error={!dashboardData?.subscriptionCounts}
            />

            {/* Titled for the period actually being shown: with period=daily
                the endpoint returns 31 days and this is today's number, which
                "Monthly Signups" would have mislabelled. */}
            <SummaryCard
              title={period === 'daily' ? 'Signups Today' : 'Monthly Signups'}
              value={formatNumber(currentBucket(signupsTrendData)?.count ?? 0)}
              subtitle={currentBucket(signupsTrendData)?.date || (period === 'daily' ? 'Today' : 'Current month')}
              icon={TrendingUp}
              loading={loading}
              error={signupsTrendData.length === 0 && !loading}
            />

            <SummaryCard
              title="Total Billing"
              value={currentBilling != null ? formatCurrency(currentBilling) : 'N/A'}
              subtitle={dashboardData?.billing ? `Invoiced ${period === 'daily' ? 'today' : 'this month'}` : 'API unavailable'}
              icon={DollarSign}
              loading={loading}
              error={dashboardData?.billing === null}
              delta={periodDelta(billingTrendData, 'amount')}
            />

            {/* new_billing was fetched on every load and rendered nowhere. For a
                subscription business the new-vs-recurring split is the cut that
                says whether growth is coming from acquisition or from the base. */}
            <SummaryCard
              title="New Billing"
              value={currentNewBilling != null ? formatCurrency(currentNewBilling) : 'N/A'}
              subtitle={
                currentNewBilling == null
                  ? 'API unavailable'
                  : ratiosComparable && currentBilling
                    ? `${((100 * currentNewBilling) / currentBilling).toFixed(0)}% of billing is new`
                    : 'This month'
              }
              icon={TrendingUp}
              loading={loading}
              error={dashboardData?.newBilling === null}
              delta={periodDelta(newBillingTrendData, 'amount')}
            />

            {/* Money COLLECTED, against Total Billing's money invoiced. It was
                labelled "Successful transactions" and read a `count` the
                endpoint has never returned, so it always showed N/A. Refunds
                ride along in the subtitle: they were fetched on every load and
                rendered nowhere. */}
            {/* Money COLLECTED, against Total Billing's money invoiced. The gap
                between the two is failed cards and dunning, and it surfaces
                here before it surfaces anywhere else. */}
            <SummaryCard
              title="Payments Collected"
              value={currentPayments != null ? formatCurrency(currentPayments) : 'N/A'}
              subtitle={
                collectionRate != null
                  ? `${collectionRate.toFixed(1)}% of invoiced collected`
                  : dashboardData?.payments ? 'This month' : 'API unavailable'
              }
              icon={CreditCard}
              loading={loading}
              error={dashboardData?.payments === null}
              delta={periodDelta(dashboardData?.payments, 'total')}
            />

            {/* Collected minus refunded — the number people mean when they ask
                what actually came in. Refunds were fetched on every load and
                shown only as six words in a subtitle. */}
            <SummaryCard
              title="Net Revenue"
              value={netRevenue != null ? formatCurrency(netRevenue) : 'N/A'}
              subtitle={
                currentRefunds
                  ? `${formatCurrency(currentRefunds)} refunded${refundRate != null ? ` · ${refundRate.toFixed(1)}% of collected` : ''}`
                  : dashboardData?.refunds ? 'No refunds this month' : 'API unavailable'
              }
              icon={Wallet}
              loading={loading}
              error={dashboardData?.payments === null}
            />
          </div>

          {/* Subscriptions & Signups Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Two donuts, because the plan name carries two independent
                facts and the old single chart conflated them into a slice per
                SKU. Drug mix answers "what are we selling"; billing frequency
                answers "how are people paying". */}
            <MixDonut
              title="Drug Mix"
              icon={Pill}
              data={drugMix}
              loading={loading}
              note={mixIsStructured
                ? undefined
                : 'Derived from plan names — Manifold billing data is unreachable, so this is approximate.'}
              emptyMessage="No subscription data available"
            />

            <MixDonut
              title="Billing Frequency"
              icon={CalendarClock}
              data={frequencyMix}
              loading={loading}
              note={mixIsStructured
                ? `${formatNumber(planMix.liveSubscriptions)} live subscriptions, by billing cycle`
                : 'Derived from plan names, which do not state a cycle reliably — connect Manifold billing data for the real split.'}
              emptyMessage="No subscription data available"
            />
          </div>

          {/* Signups on its own row now that the mix takes two cards. */}
          <div className="grid grid-cols-1 gap-6">
            {/* Signups Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Signups ({period === 'daily' ? 'Daily' : 'Monthly'})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : signupsTrendData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={signupsTrendData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <YAxis
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="count"
                          name="Signups"
                          fill="#3b82f6"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No signup data available
                  </div>
                )}

                {/* Total Signups */}
                {signupsTrendData.length > 0 && (
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <div className="text-sm text-muted-foreground">Total Signups (12 months)</div>
                    <div className="text-2xl font-bold">
                      {formatNumber(signupsTrendData.reduce((sum, item) => sum + item.count, 0))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Invoiced, collected and refunded on ONE chart.
              These were a lone line (billing) and two numbers with no chart at
              all. The gap between the top two is the story — money invoiced
              that never arrived is failed cards and dunning — and it is
              invisible while they live on separate cards.

              Refunds are drawn NEGATIVE, below the axis, because that is the
              direction the money went. */}
          {moneyTrendData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Invoiced vs Collected ({period === 'daily' ? 'Daily' : 'Monthly'})
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Payments and refunds are only ever monthly — Manifold takes no period on
                  those endpoints — so they stay monthly even when this is set to daily.
                </p>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={moneyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => formatCurrency(value, true)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <ReferenceLine y={0} className="stroke-muted-foreground" />
                      <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="refunded" name="Refunded" fill="#ef4444" radius={[0, 0, 4, 4]} />
                      <Line
                        type="monotone"
                        dataKey="billed"
                        name="Invoiced"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Which processor and merchant account every figure above actually
              ran through. Placed before Declines because an approval rate is
              read per MID, and the routing is what a decline spike is checked
              against first. */}
          <div className="pt-2">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Processing
            </h2>
            <div className="space-y-6">
              <TenderMixSection
                data={tenders}
                loading={loading}
                error={tendersError}
                formatCurrency={formatCurrency}
                formatNumber={formatNumber}
              />
              <GatewaySection
                data={gateways}
                loading={loading}
                error={gatewaysError}
                formatCurrency={formatCurrency}
                formatNumber={formatNumber}
              />
            </div>
          </div>

          {/* The gap the chart above describes but cannot show. Invoiced-minus-
              collected is failed cards; this is what they failed for, who was
              actually lost, and how much of it came back. */}
          <div className="pt-2">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Declines
              <Link to="/ops/declines" className="ml-auto text-sm font-normal text-primary hover:underline">Explore declines</Link>
            </h2>
            <DeclinesSection
              data={declines}
              loading={loading}
              error={declinesError}
              formatCurrency={formatCurrency}
              formatNumber={formatNumber}
            />
          </div>
        </>
      )}
    </div>
  )
}
